import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, mineralTitle, photoUrl, type Specimen } from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/**
 * Встроенный сканер (ТЗ 6.4, макет 5).
 *
 * Основной путь — BarcodeDetector: он есть в Chrome на Android, а это и
 * есть сценарий, ради которого сканер существует (коробка, телефон в
 * руке, десяток образцов подряд). Запасной — zxing-wasm, он подгружается
 * только если BarcodeDetector нет: почти мегабайт wasm незачем возить в
 * телефон, где он не нужен.
 *
 * Главная кнопка после попадания — «Следующий», а не «Открыть»: коробку
 * проверяют подряд.
 */

/** Что нужно показать о найденном образце — ровно это, и ничего больше. */
type Found = {
  id: string;
  inv_number: string;
  name: string;
  place: string;
  cover_photo_id: string | null;
};

function found(specimen: Specimen): Found {
  return {
    id: specimen.id,
    inv_number: specimen.inv_number,
    name:
      mineralTitle(specimen.minerals.find((m) => m.is_primary)) ||
      specimen.trade_name ||
      'без названия',
    place: specimen.locality_name ?? '',
    cover_photo_id: specimen.cover_photo_id,
  };
}

type Detector = { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> };

/**
 * Из ссылки с этикетки нужен только код: https://druzza.ru/s/AB12CD34EF.
 *
 * Регистр не фиксирован намеренно: в QR ссылка печатается заглавными
 * целиком (ТЗ 6.1 и миграция 0003), а руками её могут набрать строчными.
 */
function codeFromScan(raw: string): string | null {
  const direct = raw.trim();
  const match = /\/s\/([A-Za-z0-9]{4,32})\/?$/i.exec(direct);
  if (match) return match[1]!;
  // Иногда сканер отдаёт просто код — например, если этикетку переснимали.
  if (/^[A-Za-z0-9]{6,32}$/.test(direct)) return direct;
  return null;
}

export function ScannerScreen() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<Detector | null>(null);
  const runningRef = useRef(false);

  const [state, setState] = useState<'starting' | 'scanning' | 'hit' | 'error'>('starting');
  const [error, setError] = useState<string | null>(null);
  const [hit, setHit] = useState<Found | null>(null);
  const [seen, setSeen] = useState<Found[]>([]);
  const [manual, setManual] = useState('');

  const stop = useCallback(() => {
    runningRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  /** Готовит распознаватель: штатный, а если его нет — zxing. */
  const makeDetector = useCallback(async (): Promise<Detector> => {
    const native = (window as unknown as { BarcodeDetector?: new (o: unknown) => Detector })
      .BarcodeDetector;
    if (native) return new native({ formats: ['qr_code'] });

    const [{ readBarcodes, prepareZXingModule }, wasmUrl] = await Promise.all([
      import('zxing-wasm/reader'),
      import('zxing-wasm/reader/zxing_reader.wasm?url').then((m) => m.default as string),
    ]);
    // Wasm берётся со своего же адреса: чужой CDN здесь означал бы, что
    // сканер не работает без интернета и зависит от постороннего сервиса.
    prepareZXingModule({ overrides: { locateFile: () => wasmUrl }, fireImmediately: false });

    return {
      async detect(source: CanvasImageSource) {
        const canvas = canvasRef.current!;
        const context = canvas.getContext('2d')!;
        const width = canvas.width;
        const height = canvas.height;
        context.drawImage(source, 0, 0, width, height);
        const results = await readBarcodes(context.getImageData(0, 0, width, height), {
          formats: ['QRCode'],
          tryHarder: true,
        });
        return results.map((r) => ({ rawValue: r.text }));
      },
    };
  }, []);

  const onCode = useCallback(
    async (raw: string) => {
      const code = codeFromScan(raw);
      if (!code) return false;

      try {
        const card = found(await api.specimenByPublicId(code));
        // Короткая вибрация вместо звука: сканируют молча, коробку за коробкой.
        navigator.vibrate?.(50);
        setHit(card);
        setSeen((current) =>
          current.some((s) => s.id === card.id) ? current : [card, ...current].slice(0, 8),
        );
        setState('hit');
        return true;
      } catch {
        // Код прочитан, но образца нет: этикетка от удалённого образца
        // или чужой QR. Сообщаем и продолжаем сканировать (ТЗ 6.4).
        setError(`Код ${code} прочитан, но такого образца нет`);
        return false;
      }
    },
    [],
  );

  const start = useCallback(async () => {
    setError(null);
    setState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play();

      const canvas = canvasRef.current!;
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      detectorRef.current ??= await makeDetector();
      setState('scanning');
      runningRef.current = true;

      const tick = async () => {
        if (!runningRef.current) return;
        try {
          const codes = await detectorRef.current!.detect(video);
          for (const code of codes) {
            if (await onCode(code.rawValue)) {
              runningRef.current = false;
              return;
            }
          }
        } catch {
          /* кадр не распознался — обычное дело, идём дальше */
        }
        // Десять кадров в секунду: чаще — только греть телефон.
        if (runningRef.current) setTimeout(() => void tick(), 100);
      };
      void tick();
    } catch (e) {
      setState('error');
      setError(
        e instanceof DOMException && e.name === 'NotAllowedError'
          ? 'Камера запрещена. Разрешите доступ в настройках браузера — или найдите образец по номеру.'
          : 'Камера недоступна. Найдите образец по инвентарному номеру.',
      );
    }
  }, [makeDetector, onCode]);

  useEffect(() => {
    void start();
    return stop;
  }, [start, stop]);

  function next() {
    setHit(null);
    setError(null);
    void start();
  }

  return (
    <div className="app">
      <div className="viewfinder">
        <div className="viewfinder-bar">
          <button
            className="icon-btn"
            onClick={() => {
              stop();
              navigate('/');
            }}
            aria-label="Закрыть сканер"
          >
            <Icon name="x" className="icon" />
          </button>
          <span className="small">Сканирование</span>
          <Icon name="camera" className="icon" />
        </div>

        <video ref={videoRef} className="viewfinder-video" playsInline muted />
        <canvas ref={canvasRef} hidden />

        <div className="reticle">
          <i />
          <i />
          <i />
          <i />
        </div>
        <div className="viewfinder-hint">
          {state === 'starting'
            ? 'Включаем камеру…'
            : state === 'hit'
              ? 'Нашли'
              : state === 'error'
                ? 'Камера не работает'
                : 'Наведите на код на этикетке'}
        </div>
      </div>

      <div className="scroll" style={{ paddingTop: 'var(--space-4)' }}>
        {hit && (
          <>
            <Link className="hit" to={`/specimen/${hit.id}`}>
              <div className="well">
                {hit.cover_photo_id ? (
                  <img className="tile-cover" src={photoUrl(hit.cover_photo_id, true)} alt="" />
                ) : (
                  <Icon name="photo" className="icon icon-sm" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono small">{hit.inv_number}</div>
                <div style={{ fontWeight: 'var(--weight-medium)' }}>{hit.name}</div>
                <div className="hint">{hit.place || ' '}</div>
              </div>
              <Icon name="chevron" className="icon icon-sm" />
            </Link>

            <div className="actions" style={{ marginTop: 'var(--space-3)' }}>
              <button className="btn" onClick={next}>
                <Icon name="refresh" className="icon icon-sm" /> Следующий
              </button>
              <button
                className="btn btn--primary"
                onClick={() => navigate(`/specimen/${hit.id}`)}
              >
                Открыть
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="notice notice--warn">
            <Icon name="alert" className="icon icon-sm" />
            <span>{error}</span>
          </div>
        )}

        {/* Бумага теряется, краска на камне — нет (ТЗ 6.5). Поэтому поиск по
            номеру лежит прямо здесь, а не только на экране коллекции. */}
        <div className="label-key">Или найдите по номеру на камне</div>
        <form
          className="field"
          onSubmit={(e) => {
            e.preventDefault();
            if (manual.trim()) navigate(`/?q=${encodeURIComponent(manual.trim())}`);
          }}
        >
          <Icon name="search" className="icon icon-sm" />
          <input
            className="field-bare"
            placeholder="MIN-00042 или просто 42"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
          />
        </form>

        {seen.length > 0 && (
          <>
            <div className="label-key">Отсканировано только что</div>
            <div className="strip">
              {seen.map((item) => (
                <Link key={item.id} className="well" to={`/specimen/${item.id}`} title={item.inv_number}>
                  {item.cover_photo_id ? (
                    <img className="tile-cover" src={photoUrl(item.cover_photo_id, true)} alt="" />
                  ) : (
                    <Icon name="photo" className="icon icon-sm" />
                  )}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
